/**
 * Local metric frame.
 *
 * The solver must never see degrees — longitude degrees shrink with latitude,
 * so a lat/lon grid has anisotropic cells and the terrain gradients come out
 * silently wrong (SPEC §3.1). Everything below the DEM boundary works in a
 * local east-north-up tangent plane in metres, which is square by construction.
 *
 * A tangent plane rather than UTM: our domains are 2–30 km, where the planar
 * approximation is good to a few metres, and it avoids zone-boundary handling
 * entirely.
 */

export const EARTH_R = 6378137;
const D2R = Math.PI / 180;

export interface EnuFrame {
  lat0: number;
  lon0: number;
  /** metres per degree of longitude at lat0 */
  mPerDegLon: number;
  /** metres per degree of latitude */
  mPerDegLat: number;
  crs: string;
}

export function makeFrame(lat0: number, lon0: number): EnuFrame {
  const mPerDegLat = EARTH_R * D2R;
  const mPerDegLon = EARTH_R * D2R * Math.cos(lat0 * D2R);
  return {
    lat0,
    lon0,
    mPerDegLon,
    mPerDegLat,
    crs: `ENU:${lat0.toFixed(6)},${lon0.toFixed(6)}`,
  };
}

export function lonLatToEnu(f: EnuFrame, lon: number, lat: number): [number, number] {
  return [(lon - f.lon0) * f.mPerDegLon, (lat - f.lat0) * f.mPerDegLat];
}

export function enuToLonLat(f: EnuFrame, x: number, y: number): [number, number] {
  return [f.lon0 + x / f.mPerDegLon, f.lat0 + y / f.mPerDegLat];
}

export type BBox = [west: number, south: number, east: number, north: number];

/** Ground resolution of a Web Mercator tile pyramid, metres per pixel. */
export function mercatorResolution(lat: number, zoom: number, tileSize = 256): number {
  return (156543.03392804097 * Math.cos(lat * D2R)) / (Math.pow(2, zoom) * (tileSize / 256));
}

/** Smallest zoom whose pixels are at least as fine as `cellSize`. */
export function zoomForResolution(lat: number, cellSize: number, maxZoom: number): number {
  const z = Math.log2((156543.03392804097 * Math.cos(lat * D2R)) / cellSize);
  return Math.max(0, Math.min(maxZoom, Math.ceil(z)));
}

export function lonToTileX(lon: number, z: number): number {
  return ((lon + 180) / 360) * Math.pow(2, z);
}

export function latToTileY(lat: number, z: number): number {
  const s = Math.sin(lat * D2R);
  return ((0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * Math.pow(2, z));
}

export function bboxCentre(b: BBox): [number, number] {
  return [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2];
}

/** Width and height of a bbox in metres. */
export function bboxSpanMetres(b: BBox): [number, number] {
  const [, latC] = bboxCentre(b);
  const f = makeFrame(latC, b[0]);
  return [(b[2] - b[0]) * f.mPerDegLon, (b[3] - b[1]) * f.mPerDegLat];
}

/**
 * A square metric domain covering the bbox, centred on it. Square because the
 * multigrid hierarchy halves both horizontal directions and the particle
 * renderer maps the domain to a unit quad.
 */
export interface MetricDomain {
  frame: EnuFrame;
  /** metres */
  size: number;
  cellSize: number;
  gridSize: number;
  /** ENU of grid[0,0], the south-west corner */
  origin: [number, number];
  bbox: BBox;
}

export function metricDomain(bbox: BBox, gridSize: number): MetricDomain {
  const [lonC, latC] = bboxCentre(bbox);
  const frame = makeFrame(latC, lonC);
  const [wM, hM] = bboxSpanMetres(bbox);
  const size = Math.max(wM, hM);
  const cellSize = size / gridSize;
  const origin: [number, number] = [-size / 2, -size / 2];

  const halfLon = size / 2 / frame.mPerDegLon;
  const halfLat = size / 2 / frame.mPerDegLat;
  return {
    frame,
    size,
    cellSize,
    gridSize,
    origin,
    bbox: [lonC - halfLon, latC - halfLat, lonC + halfLon, latC + halfLat],
  };
}

/** Fractional grid coordinates of a lon/lat within a domain. */
export function lonLatToGrid(d: MetricDomain, lon: number, lat: number): [number, number] {
  const [x, y] = lonLatToEnu(d.frame, lon, lat);
  return [(x - d.origin[0]) / d.cellSize - 0.5, (y - d.origin[1]) / d.cellSize - 0.5];
}

export function gridToLonLat(d: MetricDomain, gx: number, gy: number): [number, number] {
  const x = d.origin[0] + (gx + 0.5) * d.cellSize;
  const y = d.origin[1] + (gy + 0.5) * d.cellSize;
  return enuToLonLat(d.frame, x, y);
}
